import { useState, useRef, useEffect } from 'react'
import { Modal, Form, Table, Input, message, Row, Col } from 'antd'
import { EllipsisOutlined, LoadingOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import { v4 as uuidv4 } from 'uuid'
import { errorMessage } from '../../../../helpers/errorMessageFunc'
import { generateOtp } from '../../../../components/formItems/verificationCode'
import { toPrice } from '../../../../helpers/stringFormat'
import { backendPostWithoutRefresh } from '../../../../helpers/callBackend'
import LoadingButton from '@mui/lab/LoadingButton'
import Button from '@mui/material/Button'
import { useUcs } from '../../../../helpers/contexts/UserCompanySettingsContext'
import PermissionGate from '../../../../helpers/PermissionGate'
import checkFlag from '../../../../helpers/featureFlags'

const statusMap = {
  inProgress: <EllipsisOutlined style={{ fontSize: '18px' }} />,
  notStarted: '',
  pending: <LoadingOutlined style={{ fontSize: '18px' }} />,
  success: <CheckCircleOutlined style={{ color: 'green', fontSize: '18px' }} />,
  error: <ExclamationCircleOutlined style={{ color: 'red', fontSize: '18px' }} />,
}

const ReviewPaymentRequests = (props) => {
  const [displayData, setDisplayData] = useState(
    props.displayData.map((payment, idx) => {
      if (idx === 0) {
        return {
          ...payment,
          status: 'inProgress',
        }
      } else {
        return {
          ...payment,
          status: 'notStarted',
        }
      }
    })
  )
  const [idempotencyKey, setIdempotencyKey] = useState(uuidv4())
  const [verificationCode, setVerificationCode] = useState()
  const [submitting, setSubmitting] = useState(false)
  const [allPaymentsBelowLimit, setAllPaymentsBelowLimit] = useState(false)
  const formRef = useRef(null)
  const ucsContext = useUcs()

  const getNewToken = async (index = 0) => {
    if (!displayData[0].sendingAccount) {
      errorMessage("Please select a 'From' bank account first.")
      return
    }

    await generateOtp()
  }

  useEffect(() => {
    const payments = displayData.slice(0, -1)
    let paymentsBelowLimit = true
    payments.forEach((payment) => {
      if (!ucsContext.checkLimit('create', payment.amount)) {
        errorMessage(`$${payment.amount} exceeds your payment request creation limit of $${ucsContext.createLimit()}`)
        paymentsBelowLimit = false
      }
    })
    setAllPaymentsBelowLimit(paymentsBelowLimit)
  }, [displayData])

  const handleChange = (e) => {
    setVerificationCode(e.target.value)
  }

  const handlePayment = async () => {
    const payments = displayData.slice(0, -1)

    let insufficientFunds = false

    payments.forEach((payment) => {
      if (payment.sendingAccount.balance < payment.amount) {
        insufficientFunds = true
      }
    })

    if (insufficientFunds) {
      errorMessage('Insufficient funds. Amount exceeds the available balance.')
      return
    }
    if (!verificationCode && checkFlag('otpPayments')) {
      errorMessage('Please enter a verification code first.')
      return
    }
    if (!ucsContext.loaded) {
      errorMessage('Could not load user permissions for verifying bulk payment request')
    } else {
    }

    const formData = {
      otp: verificationCode || '',
      payments: payments,
    }
    payments.forEach((_, index) => {
      formData.payments[index]['idempotencyKey'] = idempotencyKey + '-' + index
    })
    try {
      setSubmitting(true)
      const multiResp = await backendPostWithoutRefresh('/api/payments/payment-request/multi/', formData)
      setSubmitting(false)
      if (multiResp.status === 201) {
        message.success('Payment Requests submitted. Please see Pending Requests for details.', 5)
        // props.updateReceiptsTable(response.data.data.success, response.data.data.fail)
        props.setCreatedPaymentRequests((prevState) => [...multiResp.data?.data?.success, ...prevState])
        props.setFailedPaymentRequests((prevState) => [...multiResp.data?.data?.failure, ...prevState])
        props.closeReviewModal()
        props.resetPaymentForm()
      }
    } catch (error) {
      console.error('error: ', error)
      errorMessage(`Error: ${error}`, 0)
    }
  }

  const recipientKey = props.paymentType === 'external' ? 'receivingCounterparty' : 'receivingAccount'
  const displayHeader = [
    {
      title: 'Sending Account',
      dataIndex: 'sendingAccount',
      render(text, record) {
        if (record.method === 'Total') {
          return <span></span>
        } else {
          return <span>{text.description}</span>
        }
      },
    },
    {
      title: recipientKey === 'receivingCounterparty' ? 'Recipient' : 'Receiving Account',
      dataIndex: recipientKey,
      render(text, record) {
        if (record.method === 'Total') {
          return props.paymentType === 'external' ? <span></span> : 'Total'
        } else {
          return <span>{text.description}</span>
        }
      },
    },
    ...(props.paymentType === 'external'
      ? [
        {
          title: 'Method',
          dataIndex: 'method',
        },
      ]
      : []),
    {
      title: 'Description',
      dataIndex: 'description',
      render: (text, record) => {
        return (
          <Col style={{ display: 'flex', justifyContent: 'flex-end', flexDirection: 'column' }}>
            <Row> {record.description} </Row>
            {props.paymentType === 'external' && record.method === 'ach' && <Row> Addenda: {record.addenda} </Row>}
          </Col>
        )
      },
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      align: 'right',
      render: (text) => <span>{toPrice(text)}</span>,
    },
  ]

  return (
    <Modal title='Review Payment' width={1000} visible={props.show} footer={null} onCancel={props.closeReviewModal}>
      <Form ref={formRef} layout='vertical' name='basic' autoComplete='off'>
        <Table columns={displayHeader} dataSource={displayData} pagination={false} />
        {checkFlag('otpPayments') && (
          <Row style={{ marginTop: 20, width: '100%' }}>
            <Col span={18}>
              <Form.Item
                name='verificationCode'
                label='Verification Code'
                rules={[{ required: true, message: 'Please input the 6-digit code sent to your phone.' }]}
                onChange={handleChange}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Button
                onClick={getNewToken}
                style={{ marginLeft: 20, marginTop: 30 }}
                sx={{ fontSize: 14, textTransform: 'none', width: '200px' }}
                disableElevation
                fullWidth
                size='small'
                type='submit'
                variant='contained'
                color='primary'
              >
                Generate New Code
              </Button>
            </Col>
          </Row>
        )}
        <Form.Item>
          <PermissionGate
            condition={allPaymentsBelowLimit}
            readOnly={
              <LoadingButton
                disabled
                htmlType='submit'
                style={{ marginLeft: '30%', marginRight: '30%', marginTop: 30, width: 400 }}
                sx={{ fontSize: 14, textTransform: 'none' }}
                disableElevation
                fullWidth
                size='small'
                type='submit'
                variant='contained'
                color='primary'
              >
                Submit
              </LoadingButton>
            }
            toolTipText='One or more amounts is exceeds your payment request creation limit'
            toolTipPlacement='top'
          >
            <LoadingButton
              onClick={handlePayment}
              htmlType='submit'
              loading={submitting}
              loadingPosition='start'
              style={{ marginLeft: '30%', marginRight: '30%', marginTop: 30, width: 400 }}
              sx={{ fontSize: 14, textTransform: 'none' }}
              disableElevation
              fullWidth
              size='small'
              type='submit'
              variant='contained'
              color='primary'
            >
              Submit
            </LoadingButton>
          </PermissionGate>
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default ReviewPaymentRequests
